import Resource from './Resource';

type LookupField = string | { field: string };
type LookupSort = { [key: string]: 'asc' | 'desc' }[];

export type FindEndResponse = { data: { value: string; count: number }[]; metadata: { total: number } };
export type FindCustomValuesResponse = { data: { value: string; name: string }[]; metadata: { total: number } };

class LookupInstance {
	private resource: Lookup;
	private customerId: number;
	private type: string | null = null;
	private isCaseSensitive?: boolean;

	constructor(resource: Lookup, customerId: number) {
		this.resource = resource;
		this.customerId = customerId;
	}

	setType(type?: string) {
		this.type = type ?? null;
		return this;
	}

	setCaseSensitive() {
		this.isCaseSensitive = true;
		return this;
	}

	findEnd(
		field: LookupField,
		term: string,
		limit?: number,
		sort?: LookupSort,
		excludeValues?: string[]
	): Promise<FindEndResponse> {
		if (!this.type) {
			throw new Error('No type set for lookup resource');
		}

		if (typeof field === 'object' && Object.prototype.hasOwnProperty.call(field, 'field')) {
			field = field.field;
		}

		const params: { [key: string]: unknown } = {
			limit: limit || 10,
			q: term + '*',
			caseSensitive: this.isCaseSensitive
		};

		if (sort) {
			params.sort = sort;
		}

		if (excludeValues) {
			params.excludeValues = excludeValues;
		}

		const type = this.type;
		this.type = null;

		return this.resource._find(this.customerId, type, field as string, params);
	}

	findCustomValues(
		fieldId: number,
		term: string,
		limit?: number,
		overrideFieldBase?: string,
		fieldSuffix?: string
	): Promise<FindCustomValuesResponse> {
		if (!this.type) {
			throw new Error('No type set for lookup resource');
		}

		const params: { [key: string]: unknown } = {};
		let fieldBase = 'custom';
		if (overrideFieldBase) {
			fieldBase = overrideFieldBase;
			params[overrideFieldBase + '.fieldId'] = fieldId;
		} else {
			params.fieldId = fieldId;
		}
		params.limit = limit || 10;
		const field = fieldBase + '.' + (fieldSuffix || 'value');
		if (term) {
			params.q = term + '*';
		}

		const type = this.type;
		this.type = null;

		return this.resource._find(this.customerId, type, field, params);
	}
}

class Lookup extends Resource {
	allTypes = {
		ACCOUNT: 'client',
		CONTACT: 'contact',
		KEYWORD: 'keyword',
		APPOINTMENT: 'appointment',
		SOLIDITETCLIENT: 'soliditetClient'
	};

	constructor() {
		super('lookup');
	}

	customer(customerId: number) {
		return new LookupInstance(this, customerId);
	}

	_find<T>(customerId: number, type: string, field: string, params: { [key: string]: unknown }): Promise<T> {
		return this._getRequest(`${type}/${field}`, {
			_url: `${customerId}/lookup`,
			params,
			methodName: 'find'
		}).then(res => res.data);
	}
}

const resource = new Lookup();

export default resource;
